<template>
  <form @submit.prevent="onSubmit" class="p-fluid">
    <MnRow>
      <MnColumn cols="12">
        <ValidatedInput
          id="name"
          label="Entity Name"
          v-model="form.state.data.name"
          :validation="form.state.$v.name"
          :inputComponent="InputText"
          :inputAttrs="{ placeholder: 'Entity Name' }"
          autofocus
        />
      </MnColumn>
    </MnRow>
    <MnRow>
      <MnColumn cols="12">
        <ValidatedInput
          id="type"
          label="Entity Type"
          v-model="form.state.data.type"
          :validation="form.state.$v.type"
          :inputComponent="Select"
          :inputAttrs="{
            inputId: 'type',
            ariaLabel: 'Entity Type',
            options: entityTypes,
            optionLabel: 'label',
            optionValue: 'value',
            placeholder: 'Select Entity Type'
          }"
        />
      </MnColumn>
    </MnRow>

    <MnRow v-if="form.state.data.type">
      <MnColumn cols="12" v-if="form.state.data.type === 'PBM'">
        <ValidatedInput
          id="pbmId"
          label="PBM ID"
          v-model="form.state.data.pbmId"
          :validation="form.state.$v.pbmId"
          :inputComponent="InputText"
          :inputAttrs="{ placeholder: 'PBM ID' }"
        />
      </MnColumn>
      <MnColumn cols="12" v-else>
        <ValidatedInput
          id="naicId"
          label="NAIC ID"
          v-model="form.state.data.naicId"
          :validation="form.state.$v.naicId"
          :inputComponent="InputText"
          :inputAttrs="{ placeholder: 'NAIC ID' }"
        />
      </MnColumn>
    </MnRow>


    <MnRow>
      <MnColumn cols="12">
        <ValidatedInput
          id="status"
          label="Entity Status"
          v-model="form.state.data.status"
          :validation="form.state.$v.status"
          :inputComponent="Select"
          :inputAttrs="{
            inputId: 'status',
            ariaLabel: 'Entity Status',
            options: statuses,
            optionLabel: 'label',
            optionValue: 'value',
            placeholder: 'Select Entity Status'
          }"
        />
      </MnColumn>
    </MnRow>

    <div class="d-flex justify-content-end">
      <Button type="button" class="me-2" rounded raised @click="emit('cancel')" label="Cancel" severity="secondary" />
      <Button
        type="submit"
        icon="pi pi-plus"
        rounded
        raised
        label="Add Entity"
        severity="primary"
      />
    </div>
  </form>
</template>

<script setup>
import InputText from 'primevue/inputtext';
import Select from 'primevue/select';
import Button from 'primevue/button';

import {ENTITY_TYPES, ENTITY_STATUSES, TOAST_SAVE_LIFE, getAsFormOptions} from "@/utils/constants";
import { createEntity } from "@/services/entityService";
import { EntityModel } from "@/models/EntityModel";
import { useToast } from 'primevue/usetoast';
import { useAppStore } from "@/stores/appStore";
import ValidatedInput from "@/components/common/ValidatedInput.vue";
import MnRow from "@/components/common/MnRow.vue";
import MnColumn from "@/components/common/MnColumn.vue";
import {useForm} from "@/composables/form.js";
import {computed} from "vue";
import {createEntityValidationRules} from "@/validators/entities/createEntityValidationRules.js";

const emit = defineEmits(["entity-added","cancel"]);
const toast = useToast();
const appStore = useAppStore();

const model = new EntityModel();
const rules = computed(() => createEntityValidationRules(appStore.getEntitiesAll()));
const form = useForm(model, rules);

const entityTypes = getAsFormOptions(ENTITY_TYPES);
const statuses = getAsFormOptions(ENTITY_STATUSES);

const onSubmit = async () => {
  form.state.$v.$touch();
  if (form.state.$v.$invalid) {
    toast.add({ group: 'top-toast', severity: 'warn', summary: 'Validation Error', detail: 'Please fill all required fields.', life: TOAST_SAVE_LIFE });
    return;
  }

  const modelData = { ...form.state.data };
  if (modelData.type !== 'PBM') modelData.pbmId = null;
  if (modelData.type === 'PBM') modelData.naicId = null;

  const model = new EntityModel(modelData);

  try {
    const newEntity = await createEntity(model);
    await appStore.fetchEntities();
    toast.add({ group: 'top-toast', severity: 'success', summary: 'Entity Added', detail: `${newEntity.name} (${newEntity.id}) was created successfully.`, life: TOAST_SAVE_LIFE });
    emit("entity-added");
    form.state.data = new EntityModel();
    form.state.$v.$reset();
  } catch (e) {
    const errorMessage = e?.response?.data?.message ?? e?.message ?? 'Unknown error';
    toast.add({ group: 'top-toast', severity: 'error', summary: 'Error Creating Entity', detail: errorMessage, life: TOAST_SAVE_LIFE });
  }
};
</script>

<style scoped>
.field {
  margin-bottom: 1rem;
}
</style>
